import { Link, NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

/**
 * Public layout — radically simplified to a vitrine sobre.
 * Only 2 public destinations: home and contact. Login + Request access
 * are the only two CTAs. All platform/module/team/sector pages have been
 * retired by Constante Partners to make the offer feel selective.
 */
export default function PublicLayout() {
  const { t } = useTranslation();
  const NAV = [
    { to: "/", label: t("public.nav.home"), end: true },
    { to: "/platform", label: t("public.nav.platform", { defaultValue: "Plateforme" }) },
    { to: "/contact", label: t("public.nav.contact") },
  ];
  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-nav-logo">
            <div className="w-8 h-8 rounded-md bg-[#378ADD]/20 border border-[#378ADD]/40 flex items-center justify-center">
              <span className="noam-display font-bold text-[#378ADD]">N</span>
            </div>
            <span className="noam-display text-lg font-semibold tracking-tight">Noam</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`public-nav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md transition-colors ${
                    isActive ? "text-white bg-white/5" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact className="mr-1 hidden sm:inline-flex" />
            <ThemeToggle compact className="hidden sm:inline-flex" />
            <Link to="/request-access" className="btn-ghost text-xs hidden sm:inline-flex" data-testid="header-cta-request">
              {t("public.nav.request", { defaultValue: "Demander un accès" })}
            </Link>
            <Link to="/login" className="btn-primary" data-testid="header-cta-login">
              {t("public.nav.login")}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-white/5 mt-24">
        <div className="max-w-5xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-zinc-500">
            <span className="text-zinc-300 font-medium">Constante Partners</span>
            <span className="mx-2 text-zinc-700">·</span>
            <span>30 boulevard Helvétique, 1207 Genève</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-zinc-600 uppercase tracking-[0.18em]">
            <a href="/politique-confidentialite" className="hover:text-zinc-300" data-testid="footer-privacy-link">
              {t("public.footer.privacy", { defaultValue: "Politique de confidentialité" })}
            </a>
            <span className="text-zinc-700">·</span>
            <span>{t("public.footer.security", { defaultValue: "Chiffrement TLS · SCC 2021" })}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
